import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { RoundButton } from 'components/common/Button/RoundButton';
import { StyledDialogActions, StyledDialogContent, StyledDialogTitle } from 'components/common/Dialog';
import { Dispatch, Fragment, useContext, useState } from 'react';
import { setCancellationCoupon } from 'services/subscriptionService';
import { showError } from 'utils/errorHandler';
import { Close } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AuthContext } from 'contexts/AuthContext';

interface IActiveDiscountDetailsProps {
  loading: boolean;
  setLoading: Dispatch<boolean>;
  loadSubscription: () => void;
  setDiscountDialog: Dispatch<boolean>;
  handleClose: () => void;
}

export default function DowngradeDiscount({
  loading,
  setDiscountDialog,
  setLoading,
  handleClose,
  loadSubscription,
}: IActiveDiscountDetailsProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [showSuccess, setShowSuccess] = useState(false);
  const authContext = useContext(AuthContext);

  const handleCouponClick = () => {
    setLoading(true);
    setCancellationCoupon()
      .then(() => {
        setShowSuccess(true);
        setLoading(false);
      })
      .finally(() => setLoading(false))
      .catch(showError);
  };

  const closeTasks = () => {
    setLoading(true);
    authContext
      .refresh()
      .then(() => {
        loadSubscription();
        handleClose();
      })
      .finally(() => setLoading(false));
  };

  return (
    <Fragment>
      {showSuccess ? (
        <SuccessComponent handleClose={closeTasks} loading={loading} />
      ) : (
        <Fragment>
          <StyledDialogTitle>
            <Typography variant="h6" color="grey.900">
              Downgrade to Free Plan
            </Typography>
          </StyledDialogTitle>
          <StyledDialogContent dividers>
            We would love you stay with us for another month, and we would like to offer you 50% off next month to stay?
          </StyledDialogContent>
          <StyledDialogActions>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} width="100%" justifyContent="right">
              <RoundButton
                variant="outlined"
                fullWidth={isMobileScreen}
                onClick={() => setDiscountDialog(false)}
                disabled={loading}
              >
                No, I want to Cancel
              </RoundButton>
              <RoundButton
                variant="contained"
                fullWidth={isMobileScreen}
                type="submit"
                loading={loading}
                onClick={handleCouponClick}
              >
                Yes, give me 50% off
              </RoundButton>
            </Stack>
          </StyledDialogActions>
        </Fragment>
      )}
    </Fragment>
  );
}

function SuccessComponent({ handleClose, loading }: { handleClose: () => void; loading: boolean }) {
  return (
    <Fragment>
      <StyledDialogTitle>
        <Typography variant="h6" color="grey.900">
          Discount Applied
        </Typography>
        <IconButton onClick={() => handleClose()}>
          <Close />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent dividers>
        <Box>
          <Typography variant="body1" component="span">
            Thanks for choosing to stay with Shoutt! We&apos;ve applied 50% off on your next billing cycle.
          </Typography>
          <CheckCircleIcon color="success" sx={{ ml: 1, mb: -0.5 }} />
        </Box>
      </StyledDialogContent>
      <StyledDialogActions>
        <RoundButton variant="outlined" onClick={() => handleClose()} loading={loading}>
          Close
        </RoundButton>
      </StyledDialogActions>
    </Fragment>
  );
}
